/**
 * Runtime codec feature detection — TASK 8.2 Step 4 (AC 5.7).
 *
 * `packages/shared/src/codec-matrix.ts` is the VERIFIED-2026 baseline and says
 * so itself: "the CLIENT feature-detects at runtime (`HTMLMediaElement.
 * canPlayType` probe — TASK 8.2 `codec-detect.ts`); this matrix is the
 * documented baseline the probe results are validated against, never a
 * substitute for the probe." This module is that probe, and it is
 * AUTHORITATIVE IN BOTH DIRECTIONS over the server's `formatSupported` flag:
 *
 *   server says supported + probe says no  -> unsupported (an iOS 17 device
 *       cannot decode OGG however confident the verified matrix is);
 *   server says unsupported + probe says yes -> supported (the matrix marks
 *       Firefox `unverified` for three of four formats, which is a statement
 *       about the CONTRACT, not about the engine in front of the user).
 *
 * ── FORMAT NAMES ──────────────────────────────────────────────────────────
 * The catalog's `track.format` is the sync job's container classification and
 * uses `m4a` (see `apps/api/src/services/drive-proxy.ts#contentTypeForFormat`),
 * while the matrix names the CODEC `aac`. {@link normalizeFormat} is the one
 * place that reconciles them; an unrecognised container is NOT guessed.
 *
 * ── WHAT "UNKNOWN" MEANS ──────────────────────────────────────────────────
 * A format string we cannot name cannot be probed. Rather than assume, the
 * detector falls back to the SERVER's flag, and — with no flag either — to
 * `false`. AC 5.7's requirement is "never a silent undecodable stream / an
 * infinite spinner", so the safe default is to refuse and show the explicit
 * "format not supported on this device" marker instead of starting a stream
 * that will stall.
 *
 * @module
 */

import { AUDIO_FORMATS, FORMAT_MIME_TYPES, type AudioFormat } from '@streamymusic/shared';

/** The three values `HTMLMediaElement.canPlayType` is defined to return. */
export type CanPlayTypeResult = '' | 'maybe' | 'probably';

/** Container/codec aliases seen in `track.format`, mapped onto the matrix. */
const FORMAT_ALIASES: Readonly<Record<string, AudioFormat>> = {
  mp3: 'mp3',
  mpeg: 'mp3',
  mpga: 'mp3',
  m4a: 'aac',
  m4b: 'aac',
  mp4: 'aac',
  aac: 'aac',
  flac: 'flac',
  ogg: 'ogg',
  oga: 'ogg',
  vorbis: 'ogg',
};

/**
 * Maps a catalog `format` onto a matrix format, or `null` when it is not one
 * this app claims to understand.
 */
export function normalizeFormat(format: string): AudioFormat | null {
  const key = format.trim().toLowerCase();
  const alias = FORMAT_ALIASES[key];
  if (alias !== undefined) return alias;
  return (AUDIO_FORMATS as readonly string[]).includes(key) ? (key as AudioFormat) : null;
}

/** The one method this module needs from a media element. */
export interface CanPlayTypeSource {
  canPlayType(type: string): string;
}

export interface CodecDetector {
  /** Raw `canPlayType` answer, or `null` for a format we cannot name. */
  probe(format: string): CanPlayTypeResult | null;
  /**
   * AC 5.7 verdict. `serverSaid` is `albumTrackSchema.formatSupported`; it is
   * consulted ONLY when the runtime cannot be asked.
   */
  supports(format: string, serverSaid?: boolean): boolean;
}

export interface CodecDetectorDeps {
  /**
   * Probe source. Defaults to a lazily created, never-mounted `<audio>` — the
   * element is made ONCE and reused, and it is deliberately not the playback
   * element (probing must not disturb a stream that is running).
   */
  readonly source?: CanPlayTypeSource;
  /** Escape hatch for environments with no `document` (node, workers). */
  readonly createSource?: () => CanPlayTypeSource | null;
}

export function createCodecDetector(deps: CodecDetectorDeps = {}): CodecDetector {
  const {
    createSource = () =>
      typeof document === 'undefined' ? null : document.createElement('audio'),
  } = deps;

  /** `undefined` = not yet resolved; `null` = no probe available here. */
  let cached: CanPlayTypeSource | null | undefined = deps.source;
  /** Memoised per format — `canPlayType` is pure for the page lifetime. */
  const answers = new Map<AudioFormat, CanPlayTypeResult>();

  function source(): CanPlayTypeSource | null {
    if (cached === undefined) cached = createSource();
    return cached;
  }

  function probe(format: string): CanPlayTypeResult | null {
    const normalized = normalizeFormat(format);
    if (normalized === null) return null;

    const memoised = answers.get(normalized);
    if (memoised !== undefined) return memoised;

    const element = source();
    if (element === null) return null;

    let answer: CanPlayTypeResult = '';
    try {
      const raw = element.canPlayType(FORMAT_MIME_TYPES[normalized]);
      answer = raw === 'probably' || raw === 'maybe' ? raw : '';
    } catch {
      // A hardened/exotic runtime that throws is treated as "cannot decode",
      // which is the safe direction: an explicit marker, never a dead stream.
      answer = '';
    }
    answers.set(normalized, answer);
    return answer;
  }

  return {
    probe,
    supports(format, serverSaid) {
      const answer = probe(format);
      // The probe had nothing to say (unknown container, or no media element
      // in this runtime): defer to the server's verified matrix, else refuse.
      if (answer === null) return serverSaid ?? false;
      return answer !== '';
    },
  };
}

/**
 * The process-wide detector. `lib/audio-engine.ts` uses this one, which is
 * what gives the module a production call site.
 */
export const codecDetector: CodecDetector = createCodecDetector();
